import {
  CapacitorSQLite,
  SQLiteConnection,
  type SQLiteDBConnection,
} from '@capacitor-community/sqlite'
import { Capacitor } from '@capacitor/core'
import { buildAllDDL, type SqliteExecutor, type SqliteRow } from '@/data/sqliteExecutor.ts'
import { ENTITY_REPLICA_CONFIGS } from '@/data/entityReplicaConfig.generated.ts'

const CATALOG_DDL = buildAllDDL(ENTITY_REPLICA_CONFIGS)

const DB_NAME = 'atsume-catalog'
const DB_VERSION = 1

const isWeb = Capacitor.getPlatform() === 'web'

// jeep-sqlite persists by serializing the WHOLE in-memory DB into IndexedDB, so
// saveToStore is O(db size) — flushing after every transaction rewrites the entire
// (tens-of-MB) catalog per page and thrashes storage. Throttle it: at most one
// flush per interval during a sync burst, with the trailing writes captured by the
// explicit flushCatalogStore() calls (sync end / pagehide / logout).
const WEB_FLUSH_INTERVAL_MS = 3000
let lastWebFlushAt = 0
let webWritesPendingFlush = false

let dbPromise: Promise<SQLiteDBConnection> | null = null

// Web (dev-only) replica: @capacitor-community/sqlite runs sql.js in a
// jeep-sqlite web component, persisting to IndexedDB. Lets offline mode be
// tested in the browser (forceOffline) without deploying to a device. Native
// path is unchanged.
async function initWebStore(sqlite: SQLiteConnection): Promise<void> {
  const { defineCustomElements } = await import('jeep-sqlite/loader')
  await defineCustomElements(window)
  if (!document.querySelector('jeep-sqlite')) {
    const jeepEl = document.createElement('jeep-sqlite')
    jeepEl.setAttribute('wasm-path', '/assets')
    document.body.appendChild(jeepEl)
  }
  await customElements.whenDefined('jeep-sqlite')
  await sqlite.initWebStore()
}

const sqlite = new SQLiteConnection(CapacitorSQLite)

function messageOf(error: unknown): string {
  return (error instanceof Error ? error.message : String(error)).toLowerCase()
}

function isDatabaseLockedError(error: unknown): boolean {
  return messageOf(error).includes('database is locked')
}

function isConnectionExistsError(error: unknown): boolean {
  return messageOf(error).includes('already exists')
}

// A previous session torn down mid-write (logout while the catalog was syncing) can
// briefly leave the WAL write lock held while SQLite recovers the journal. Retry a
// few times before surfacing the failure.
async function openWithRetry(db: SQLiteDBConnection): Promise<void> {
  const maxAttempts = 5
  for (let attempt = 1; ; attempt += 1) {
    try {
      await db.open()
      return
    } catch (error) {
      if (attempt >= maxAttempts || !isDatabaseLockedError(error)) throw error
      await new Promise((resolve) => setTimeout(resolve, 300))
    }
  }
}

// The native plugin instance outlives a webview reload, so a connection opened by
// the previous session (logout -> login) can still exist native-side even though
// this fresh JS context's registry is empty. Adopt that connection instead of
// creating a second one: a second createConnection would spin up a second SQLite
// pool that dead-locks on the first's still-held WAL write lock. The prior context
// releases the connection on pagehide (see below) so the lock is freed in the
// native call queue before this create runs; the retrieve path is the fallback for
// a hard crash where pagehide never fired.
async function acquireConnection(): Promise<SQLiteDBConnection> {
  if ((await sqlite.isConnection(DB_NAME, false)).result) {
    return sqlite.retrieveConnection(DB_NAME, false)
  }
  try {
    return await sqlite.createConnection(DB_NAME, false, 'no-encryption', DB_VERSION, false)
  } catch (error) {
    if (isConnectionExistsError(error)) return sqlite.retrieveConnection(DB_NAME, false)
    throw error
  }
}

async function openDb(): Promise<SQLiteDBConnection> {
  if (isWeb) {
    await initWebStore(sqlite)
  }
  const db = await acquireConnection()
  // A reused connection is already open; only open a freshly created one.
  if (!(await db.isDBOpen()).result) {
    await openWithRetry(db)
  }
  for (const statement of CATALOG_DDL) {
    await db.execute(statement)
  }
  return db
}

// Release the native connection when this webview context unloads (a
// logout/login reload). The close is dispatched onto the native SQLite call
// queue synchronously here, so it lands after any in-flight writes from this
// session and before the next context's createConnection — freeing the WAL write
// lock that would otherwise strand the reopen with "database is locked".
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => {
    try {
      void sqlite.closeConnection(DB_NAME, false).catch(() => {})
    } catch {
      // No connection in this context's registry — nothing to release.
    }
    dbPromise = null
  })
}

function getDb(): Promise<SQLiteDBConnection> {
  if (!dbPromise) {
    // Drop the cached promise on failure — otherwise a rejected promise sticks
    // (`!dbPromise` is false for it) and every later call replays the same
    // rejection, so a transient open failure would never recover until restart.
    dbPromise = openDb().catch((error) => {
      dbPromise = null
      throw error
    })
  }
  return dbPromise
}

export async function getCatalogExecutor(): Promise<SqliteExecutor> {
  const db = await getDb()
  return {
    async run(sql, params) {
      await db.run(sql, params, false)
    },
    async runBatch(sql, paramsList) {
      if (paramsList.length === 0) return
      await db.executeSet([{ statement: sql, values: paramsList }], false)
    },
    async query(sql, params) {
      const result = await db.query(sql, params)
      return (result.values ?? []) as SqliteRow[]
    },
    async transaction(work) {
      await db.beginTransaction()
      try {
        await work()
        await db.commitTransaction()
      } catch (error) {
        await db.rollbackTransaction()
        throw error
      }
      // sql.js holds the DB in memory; flush to IndexedDB so the replica survives
      // a browser reload — but throttled (see WEB_FLUSH_INTERVAL_MS), since each
      // flush rewrites the whole DB. Stays inside the serialized transaction() so
      // saveToStore never races an in-flight write. Unflushed writes are persisted
      // by the explicit flushCatalogStore() calls.
      if (isWeb) {
        const now = Date.now()
        if (now - lastWebFlushAt >= WEB_FLUSH_INTERVAL_MS) {
          await sqlite.saveToStore(DB_NAME)
          lastWebFlushAt = now
          webWritesPendingFlush = false
        } else {
          webWritesPendingFlush = true
        }
      }
    },
  }
}

// Close the catalog connection cleanly. Called on logout BEFORE the webview
// reloads: awaiting the in-flight open (if any) then closing guarantees no open
// transaction survives into the next session to strand the DB's write lock. Safe
// to call when nothing is open (no-ops).
export async function closeCatalogStore(): Promise<void> {
  // Persist any writes the throttle deferred before dropping the connection, or
  // they're lost and the next session re-syncs/re-bootstraps them.
  if (isWeb && dbPromise && webWritesPendingFlush) {
    try {
      await sqlite.saveToStore(DB_NAME)
      lastWebFlushAt = Date.now()
      webWritesPendingFlush = false
    } catch {
      // Best effort — the reopen will re-sync from the last persisted cursor.
    }
  }
  const pending = dbPromise
  dbPromise = null
  try {
    if (pending) await pending
  } catch {
    // Open failed — nothing to close.
  }
  try {
    if ((await sqlite.isConnection(DB_NAME, false)).result) {
      await sqlite.closeConnection(DB_NAME, false)
    }
  } catch {
    // Already closed / never created.
  }
}

// Persist the in-memory web DB to IndexedDB. Bare run() writes (the KV meta:
// cursor, lastSyncedAt, version/subscribedTypes stamps) are only flushed by
// transaction(); register this on pagehide once so they survive a reload without
// flushing after every write. No-op on native — run() autocommits to the file.
export async function flushCatalogStore(): Promise<void> {
  if (!isWeb || !dbPromise) return
  await sqlite.saveToStore(DB_NAME)
  lastWebFlushAt = Date.now()
  webWritesPendingFlush = false
}
